import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, isRefreshFor } from './AuthContext';
import EmailComposer from './EmailComposer';
import ListSkeleton from './LoadingSkeleton';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  CopyIcon,
  FileIcon,
  ForwardIcon,
  InfoIcon,
  MoreIcon,
  ReplyIcon,
  PaperclipIcon,
} from './Icons';
import { avatarColor, avatarInitials, formatFileSize } from './emailAvatar';
import {
  EMAIL_CATEGORIES,
  EMAIL_PRIORITIES,
  EMAIL_STATUS,
  categoryLabel,
  forwardSubject,
  fullTimestamp,
  htmlNeedsRichView,
  isEmailKey,
  linkifyText,
  listTimestamp,
  priorityLabel,
  relativeTime,
  replySubject,
  splitQuotedText,
} from './emailUtils';

// Safety net in case a live update is missed. Only while the tab is visible.
const FALLBACK_POLL_MS = 30000;
const PAGE_SIZE = 30;
// Older messages beyond this many are folded behind one "N earlier" row.
const FOLD_AFTER = 6;

const byTime = (a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : a.id - b.id);

// Union by id, newer copy wins, oldest first.
const mergeMessages = (current, incoming) => {
  const map = new Map(current.map((message) => [message.id, message]));
  incoming.forEach((message) => map.set(message.id, message));
  return [...map.values()].sort(byTime);
};

export function StatusPill({ status }) {
  const meta = EMAIL_STATUS[status];
  if (!meta) return null;
  return <span className={`ew-status ew-status-${meta.className}`}>{meta.short || meta.label}</span>;
}

export function PriorityTag({ priority }) {
  if (!priority || priority === 'normal') return null;
  return <span className={`ew-priority ew-priority-${priority}`}>{priorityLabel(priority)}</span>;
}

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

// The email's HTML in a sandboxed frame: no scripts, no forms, no access to
// the CRM. Links open in a new tab. The frame can't report its own height
// (that would need scripts), so it gets an estimate, with "Show all" for
// long mail.
function HtmlBody({ html, text }) {
  const [tall, setTall] = useState(false);
  const estimate = useMemo(() => {
    const plainLength = (text || String(html).replace(/<[^>]+>/g, ' ')).length;
    const lines = Math.ceil(plainLength / 90) + (String(text || '').match(/\n/g) || []).length;
    const images = (String(html).match(/<img\b/gi) || []).length;
    return Math.min(560, Math.max(140, 60 + lines * 20 + images * 180));
  }, [html, text]);
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
    body{margin:0;padding:4px 2px;font:14px/1.5 -apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1f2937;word-wrap:break-word;overflow-wrap:anywhere}
    img{max-width:100%;height:auto} table{max-width:100%} blockquote{margin:0 0 0 .6em;padding-left:.6em;border-left:2px solid #d1d5db;color:#4b5563}
    </style></head><body>${html}</body></html>`;
  return (
    <div className="ew-html-body">
      <iframe
        title="Email content"
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcDoc={srcDoc}
        className="ew-html-frame"
        style={{ height: tall ? '75vh' : `${estimate}px` }}
      />
      <button type="button" className="link-btn ew-html-toggle" onClick={() => setTall((value) => !value)}>
        {tall ? 'Show less' : 'Show all'}
      </button>
    </div>
  );
}

function TextBody({ text }) {
  const [showQuoted, setShowQuoted] = useState(false);
  const { main, quoted } = useMemo(() => splitQuotedText(text), [text]);
  const render = (value) =>
    linkifyText(value).map((part, index) =>
      part.type === 'link' ? (
        <a key={index} href={part.value} target="_blank" rel="noopener noreferrer">
          {part.value}
        </a>
      ) : (
        <React.Fragment key={index}>{part.value}</React.Fragment>
      )
    );
  return (
    <div className="ew-text-body">
      {main ? render(main) : <em className="ew-muted">(no text)</em>}
      {quoted && (
        <>
          <button type="button" className="ew-quoted-toggle" onClick={() => setShowQuoted((value) => !value)}>
            {showQuoted ? 'Hide quoted text' : '··· Show quoted text'}
          </button>
          {showQuoted && <div className="ew-quoted">{render(quoted)}</div>}
        </>
      )}
    </div>
  );
}

function MessageBody({ message }) {
  const hasHtml = Boolean(message.body_html);
  const preferHtml = hasHtml && (!message.body_text || htmlNeedsRichView(message.body_html));
  const [showHtml, setShowHtml] = useState(preferHtml);
  return (
    <>
      {showHtml && hasHtml ? <HtmlBody html={message.body_html} text={message.body_text} /> : <TextBody text={message.body_text} />}
      {hasHtml && message.body_text && (
        <button type="button" className="link-btn ew-view-toggle" onClick={() => setShowHtml((value) => !value)}>
          {showHtml ? 'View as plain text' : 'View original formatting'}
        </button>
      )}
    </>
  );
}

const senderLabel = (message, currentUsername) => {
  if (message.direction === 'inbound') return message.from_name || message.from_address || 'Customer';
  if (message.sent_by === 'Webmail') return 'Support (webmail)';
  if (message.sent_by && message.sent_by === currentUsername) return 'You';
  return message.sent_by || 'Support';
};

function EmailMessage({ message, expanded, onToggle, onReply, onForward, ticket, currentUsername }) {
  const sender = senderLabel(message, currentUsername);
  const outbound = message.direction === 'outbound';
  const avatarSeed = outbound ? message.sent_by || 'support' : message.from_address;
  const attachments = Array.isArray(message.attachments) ? message.attachments : [];
  const preview = useMemo(() => splitQuotedText(message.body_text).main.replace(/\s+/g, ' ').slice(0, 160), [message.body_text]);

  if (!expanded) {
    return (
      <button
        type="button"
        className={`ew-msg ew-msg-collapsed ${outbound ? 'ew-msg-out' : 'ew-msg-in'}`}
        onClick={onToggle}
        aria-expanded="false"
      >
        <span className="ew-avatar ew-avatar-sm" style={{ backgroundColor: avatarColor(avatarSeed) }} aria-hidden="true">
          {avatarInitials(outbound ? null : message.from_name, sender)}
        </span>
        <span className="ew-msg-collapsed-sender">{sender}</span>
        <span className="ew-msg-collapsed-preview">{preview || message.subject || '(no text)'}</span>
        {attachments.length > 0 && <PaperclipIcon width={12} height={12} className="ew-muted" aria-label="Has attachments" />}
        <span className="ew-msg-time" title={fullTimestamp(message.created_at)}>
          {listTimestamp(message.created_at)}
        </span>
      </button>
    );
  }

  return (
    <article className={`ew-msg ew-msg-expanded ${outbound ? 'ew-msg-out' : 'ew-msg-in'}`} aria-label={`Email from ${sender}`}>
      <header className="ew-msg-head">
        <span className="ew-avatar ew-avatar-sm" style={{ backgroundColor: avatarColor(avatarSeed) }} aria-hidden="true">
          {avatarInitials(outbound ? null : message.from_name, sender)}
        </span>
        <button type="button" className="ew-msg-head-main" onClick={onToggle} aria-expanded="true" title="Collapse">
          <span className="ew-msg-sender">
            {sender}
            {!outbound && message.from_name && message.from_address && <span className="ew-muted"> &lt;{message.from_address}&gt;</span>}
          </span>
          <span className="ew-msg-to ew-muted">to {outbound ? message.to_address : message.to_address || 'support'}</span>
        </button>
        <span className="ew-msg-time" title={fullTimestamp(message.created_at)}>
          {fullTimestamp(message.created_at)}
        </span>
        <span className="ew-msg-actions">
          <button type="button" className="ew-icon-btn" onClick={() => onReply(message)} title="Reply" aria-label="Reply">
            <ReplyIcon width={15} height={15} />
          </button>
          <button type="button" className="ew-icon-btn" onClick={() => onForward(message)} title="Forward" aria-label="Forward">
            <ForwardIcon width={15} height={15} />
          </button>
        </span>
      </header>
      {message.subject && <div className="ew-msg-subject">{message.subject}</div>}
      <div className="ew-msg-body">
        <MessageBody message={message} />
      </div>
      {attachments.length > 0 && (
        <div className="ew-msg-files">
          {attachments.map((attachment, index) => (
            <a
              key={`${message.id}-${index}`}
              className="ew-file-chip ew-file-chip-link"
              href={`/api/email/messages/${message.id}/attachments/${index}${ticket ? `?ticket=${encodeURIComponent(ticket)}` : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open ${attachment.filename || 'attachment'}`}
            >
              <FileIcon width={13} height={13} />
              <span className="ew-file-name">{attachment.filename || 'Attachment'}</span>
              {attachment.size != null && <span className="ew-file-meta">{formatFileSize(attachment.size)}</span>}
            </a>
          ))}
        </div>
      )}
      {outbound && message.sent_by && (
        <div className="ew-msg-foot ew-muted">
          {message.sent_by === 'Webmail' ? 'Sent from Hostinger webmail' : `Sent by ${message.sent_by}`}
          {message.in_reply_to ? ' · threaded reply' : ''}
        </div>
      )}
    </article>
  );
}

function ThreadMenu({ conversation, currentUser, onChange, onMarkUnread, onClose }) {
  const rootRef = useRef(null);
  useEffect(() => {
    const handle = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) onClose();
    };
    const handleKey = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('mousedown', handle);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);
  const assignedToMe = conversation?.assigned_user_id === currentUser?.id;
  return (
    <div className="ew-menu" ref={rootRef} role="menu">
      <button type="button" className="ew-menu-item" role="menuitem" onClick={onMarkUnread}>
        Mark as unread
      </button>
      <button
        type="button"
        className="ew-menu-item"
        role="menuitem"
        onClick={() => onChange({ assignedUserId: assignedToMe ? null : currentUser?.id })}
      >
        {assignedToMe ? 'Unassign me' : 'Assign to me'}
      </button>
      <div className="ew-menu-section">
        <span className="ew-menu-label">Priority</span>
        <div className="ew-segmented" role="group" aria-label="Priority">
          {EMAIL_PRIORITIES.map((item) => (
            <button
              type="button"
              key={item.value}
              className={(conversation?.priority || 'normal') === item.value ? 'ew-segmented-active' : ''}
              onClick={() => onChange({ priority: item.value })}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div className="ew-menu-section">
        <label className="ew-menu-label" htmlFor="ew-menu-category">
          Category
        </label>
        <select
          id="ew-menu-category"
          value={conversation?.category || ''}
          onChange={(event) => onChange({ category: event.target.value || null })}
        >
          <option value="">None</option>
          {EMAIL_CATEGORIES.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// One conversation: header (who, status, actions), the messages (older ones
// folded, the newest open), and the reply area. Remounted per conversation
// (EmailOverview passes key={conversationKey}).
function EmailThread({
  conversationKey,
  summary,
  signature,
  onConversationUpdate,
  onMovedTo,
  onRead,
  onMarkedUnread,
  onBack,
  panelOpen,
  onTogglePanel,
  composeRequest,
}) {
  const { authFetch, subscribeRefresh, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState(null);
  const [conversation, setConversation] = useState(summary || null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [showFolded, setShowFolded] = useState(false);
  const [composer, setComposer] = useState(null); // { mode, message }
  const [notice, setNotice] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [stateError, setStateError] = useState('');
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef(null);
  const syncRef = useRef({ inFlight: false, queued: false, loadedOnce: false, olderLoaded: false, seenIds: new Set() });

  const markRead = useCallback(async () => {
    try {
      await authFetch(`/api/email/conversations/${encodeURIComponent(conversationKey)}/read`, { method: 'POST' });
    } catch {
      // The next load tries again.
    }
    window.dispatchEvent(new Event('email-read'));
    onRead?.(conversationKey);
  }, [authFetch, conversationKey, onRead]);

  const scrollToBottom = () =>
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });

  // Loads the newest page and merges it in. One request at a time; events
  // arriving meanwhile fold into a single follow-up.
  const loadLatest = useCallback(async () => {
    const sync = syncRef.current;
    if (sync.inFlight) {
      sync.queued = true;
      return;
    }
    sync.inFlight = true;
    try {
      const response = await authFetch(
        `/api/customers/${encodeURIComponent(conversationKey)}/email-messages?limit=${PAGE_SIZE}`
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load messages');
      if (data.movedTo) {
        onMovedTo?.(data.movedTo);
        return;
      }
      const el = scrollRef.current;
      const nearBottom = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 160;
      const firstLoad = !sync.loadedOnce;
      const fresh = data.messages.filter((message) => !sync.seenIds.has(message.id));
      fresh.forEach((message) => sync.seenIds.add(message.id));

      setMessages((current) => mergeMessages(current, data.messages));
      if (!sync.olderLoaded) {
        setHasMore(data.hasMore);
        setCursor(data.nextCursor);
      }
      setTicket(data.attachmentTicket || null);
      if (data.conversation) {
        setConversation(data.conversation);
        onConversationUpdate?.(data.conversation);
      }
      setExpanded((current) => {
        const next = new Set(current);
        if (firstLoad) {
          const unread = data.conversation?.unread_count || 0;
          const latest = data.messages[data.messages.length - 1];
          if (latest) next.add(latest.id);
          data.messages
            .filter((message) => message.direction === 'inbound')
            .slice(-unread)
            .forEach((message) => unread > 0 && next.add(message.id));
        } else {
          fresh.forEach((message) => next.add(message.id));
        }
        return next;
      });
      setError('');
      sync.loadedOnce = true;
      if (firstLoad || nearBottom) scrollToBottom();
      if (data.conversation?.unread_count > 0 && document.visibilityState === 'visible') markRead();
    } catch (err) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
      sync.inFlight = false;
      if (sync.queued) {
        sync.queued = false;
        loadLatest();
      }
    }
  }, [authFetch, conversationKey, markRead, onConversationUpdate, onMovedTo]);

  useEffect(() => {
    loadLatest();
    const unsubscribe = subscribeRefresh((event) => {
      if (!isRefreshFor(event, 'email')) return;
      if (event?.type === 'merged' && event.from === conversationKey) {
        onMovedTo?.(event.customerNumber);
        return;
      }
      if (event?.customerNumber && event.customerNumber !== conversationKey) return;
      // Marking read changes nothing on screen here.
      if (event?.type === 'read') return;
      loadLatest();
    });
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadLatest();
    }, FALLBACK_POLL_MS);
    const handleVisible = () => document.visibilityState === 'visible' && loadLatest();
    document.addEventListener('visibilitychange', handleVisible);
    return () => {
      unsubscribe();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisible);
    };
    // eslint-disable-next-line
  }, [conversationKey]);

  const loadOlder = async () => {
    if (!cursor || loadingOlder) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const previousHeight = el?.scrollHeight || 0;
    try {
      const response = await authFetch(
        `/api/customers/${encodeURIComponent(conversationKey)}/email-messages?limit=${PAGE_SIZE}&before=${encodeURIComponent(cursor)}`
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load older messages');
      syncRef.current.olderLoaded = true;
      data.messages.forEach((message) => syncRef.current.seenIds.add(message.id));
      setMessages((current) => mergeMessages(current, data.messages));
      setHasMore(data.hasMore);
      setCursor(data.nextCursor);
      setShowFolded(true);
      // Keep the view where it was rather than jumping.
      requestAnimationFrame(() => {
        if (el) el.scrollTop += el.scrollHeight - previousHeight;
      });
    } catch (err) {
      setError(err.message || 'Failed to load older messages');
    } finally {
      setLoadingOlder(false);
    }
  };

  const view = conversation || summary || { number: conversationKey };
  const name = view.name || view.email || conversationKey;
  const email = view.email || (isEmailKey(conversationKey) ? conversationKey : '');
  const firstName = String(view.name || '').trim().split(/\s+/)[0] || '';
  const latestInbound = useMemo(() => [...messages].reverse().find((message) => message.direction === 'inbound'), [messages]);
  const latest = messages[messages.length - 1];
  const lastSeenMessageId = messages.reduce((max, message) => Math.max(max, message.id), 0) || null;

  const toggle = (id) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openReply = (message = latestInbound || latest) => {
    setNotice(null);
    setComposer({ mode: 'reply', message: message || null });
  };
  const openForward = (message = latest) => {
    if (!message) return;
    setNotice(null);
    setComposer({ mode: 'forward', message });
  };

  // "Reply" / "Forward" asked for from outside (keyboard shortcut in the list).
  useEffect(() => {
    if (!composeRequest || loading) return;
    if (composeRequest.mode === 'forward') openForward();
    else openReply();
    // eslint-disable-next-line
  }, [composeRequest, loading]);

  const handleSent = (result) => {
    const forwardedElsewhere = composer?.mode === 'forward' && result.conversationKey !== conversationKey;
    setComposer(null);
    if (result.notRecorded) {
      setNotice({ text: 'The email was sent, but the CRM could not record it. It is in the Sent folder (Mailbox tab).', tone: 'warning' });
      loadLatest();
      return;
    }
    if (!forwardedElsewhere) {
      syncRef.current.seenIds.add(result.id);
      setMessages((current) => mergeMessages(current, [result]));
      setExpanded((current) => new Set(current).add(result.id));
      scrollToBottom();
    }
    const skipped = result.skippedAttachments || [];
    if (forwardedElsewhere || skipped.length > 0) {
      setNotice({
        text: [
          forwardedElsewhere ? `Forwarded to ${result.to_address}.` : null,
          skipped.length > 0
            ? `Not attached: ${skipped.map((item) => `${item.filename} (${item.reason})`).join('; ')}`
            : null,
        ]
          .filter(Boolean)
          .join(' '),
        tone: skipped.length > 0 ? 'warning' : 'info',
      });
    }
    loadLatest();
  };

  const updateState = async (changes) => {
    setMenuOpen(false);
    setStateError('');
    try {
      const response = await authFetch(`/api/email/conversations/${encodeURIComponent(conversationKey)}/state`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not update the conversation');
      setConversation(data);
      onConversationUpdate?.(data);
    } catch (err) {
      setStateError(err.message);
    }
  };

  const markUnread = async () => {
    setMenuOpen(false);
    try {
      const response = await authFetch(`/api/email/conversations/${encodeURIComponent(conversationKey)}/unread`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Could not mark as unread');
      window.dispatchEvent(new Event('email-read'));
      onMarkedUnread?.(conversationKey);
    } catch (err) {
      setStateError(err.message);
    }
  };

  const handleCopyEmail = async () => {
    if (email && (await copyText(email))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  // Which messages show: all when few; otherwise the first, a fold, and the
  // last few (anything the agent opened in the fold stays visible).
  const visible = useMemo(() => {
    if (showFolded || messages.length <= FOLD_AFTER) return { head: messages, folded: 0, tail: [] };
    const head = messages.slice(0, 1);
    const tail = messages.slice(-4);
    const middle = messages.slice(1, -4);
    if (middle.some((message) => expanded.has(message.id))) return { head: messages, folded: 0, tail: [] };
    return { head, folded: middle.length, tail };
  }, [messages, showFolded, expanded]);

  const templateValues = {
    customer_name: view.name || '',
    customer_email: email,
    order_number: view.order_number || '',
    agent_name: user?.agentName || user?.username || '',
  };

  const status = view.status;
  const resolved = status === 'resolved';

  return (
    <section className="ew-thread" aria-label={`Conversation with ${name}`}>
      <header className="ew-thread-header">
        <div className="ew-thread-ident">
          {onBack && (
            <button type="button" className="ew-icon-btn ew-back-btn" onClick={onBack} aria-label="Back to conversations">
              <ArrowLeftIcon width={16} height={16} />
            </button>
          )}
          <span className="ew-avatar" style={{ backgroundColor: avatarColor(email || conversationKey) }} aria-hidden="true">
            {avatarInitials(view.name, email || conversationKey)}
          </span>
          <div className="ew-thread-ident-text">
            <div className="ew-thread-name-row">
              <h2 className="ew-thread-name" title={name}>
                {name}
              </h2>
              <StatusPill status={status} />
              <PriorityTag priority={view.priority} />
              {view.category && <span className="ew-tag">{categoryLabel(view.category)}</span>}
            </div>
            <div className="ew-thread-meta">
              {email && (
                <button type="button" className="ew-copy-inline" onClick={handleCopyEmail} title="Copy email address">
                  {email}
                  <CopyIcon width={11} height={11} />
                  {copied && <span className="ew-copied">Copied</span>}
                </button>
              )}
              {view.last_subject && (
                <span className="ew-thread-subject" title={view.last_subject}>
                  {view.last_subject}
                </span>
              )}
              <span>
                {view.message_count || messages.length} message{(view.message_count || messages.length) === 1 ? '' : 's'}
              </span>
              {view.last_message_at && <span title={fullTimestamp(view.last_message_at)}>{relativeTime(view.last_message_at)}</span>}
              <span>{view.assigned_name ? `Assigned to ${view.assigned_name}` : 'Unassigned'}</span>
            </div>
          </div>
        </div>
        <div className="ew-thread-actions">
          <button type="button" className="btn btn-primary btn-small" onClick={() => openReply()} disabled={loading || messages.length === 0}>
            <ReplyIcon width={14} height={14} />
            Reply
          </button>
          <button
            type="button"
            className="ew-icon-btn"
            onClick={() => openForward()}
            disabled={loading || messages.length === 0}
            title="Forward the latest email"
            aria-label="Forward"
          >
            <ForwardIcon width={15} height={15} />
          </button>
          {view.number && messages.length > 0 && (
            <button
              type="button"
              className={`ew-icon-btn ${resolved ? 'ew-icon-btn-active' : ''}`}
              onClick={() => updateState({ resolved: !resolved })}
              title={resolved ? 'Reopen' : 'Mark resolved'}
              aria-label={resolved ? 'Reopen' : 'Mark resolved'}
            >
              <CheckCircleIcon width={15} height={15} />
            </button>
          )}
          <span className="ew-menu-anchor">
            <button
              type="button"
              className="ew-icon-btn"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title="More"
              disabled={messages.length === 0}
            >
              <MoreIcon width={15} height={15} />
            </button>
            {menuOpen && (
              <ThreadMenu
                conversation={view}
                currentUser={user}
                onChange={updateState}
                onMarkUnread={markUnread}
                onClose={() => setMenuOpen(false)}
              />
            )}
          </span>
          {onTogglePanel && (
            <button
              type="button"
              className={`ew-icon-btn ${panelOpen ? 'ew-icon-btn-active' : ''}`}
              onClick={onTogglePanel}
              aria-pressed={Boolean(panelOpen)}
              title={panelOpen ? 'Hide customer details' : 'Show customer details'}
              aria-label="Customer details"
            >
              <InfoIcon width={15} height={15} />
            </button>
          )}
        </div>
      </header>

      {stateError && <div className="ew-banner ew-banner-error">{stateError}</div>}
      {notice && (
        <div className={`ew-banner ${notice.tone === 'warning' ? 'ew-banner-warning' : ''}`}>
          {notice.text}
          <button type="button" className="link-btn" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="ew-thread-scroll" ref={scrollRef}>
        {loading ? (
          <ListSkeleton rows={3} />
        ) : error && messages.length === 0 ? (
          <div className="ew-banner ew-banner-error">
            {error}
            <button type="button" className="link-btn" onClick={loadLatest}>
              Try again
            </button>
          </div>
        ) : messages.length === 0 ? (
          <p className="ew-empty">No emails in this conversation yet.</p>
        ) : (
          <>
            {hasMore && (
              <button type="button" className="ew-load-older" onClick={loadOlder} disabled={loadingOlder}>
                {loadingOlder ? 'Loading…' : 'Load earlier emails'}
              </button>
            )}
            {visible.head.map((message) => (
              <EmailMessage
                key={message.id}
                message={message}
                expanded={expanded.has(message.id)}
                onToggle={() => toggle(message.id)}
                onReply={openReply}
                onForward={openForward}
                ticket={ticket}
                currentUsername={user?.username}
              />
            ))}
            {visible.folded > 0 && (
              <button type="button" className="ew-fold" onClick={() => setShowFolded(true)}>
                {visible.folded} earlier email{visible.folded === 1 ? '' : 's'}
              </button>
            )}
            {visible.tail.map((message) => (
              <EmailMessage
                key={message.id}
                message={message}
                expanded={expanded.has(message.id)}
                onToggle={() => toggle(message.id)}
                onReply={openReply}
                onForward={openForward}
                ticket={ticket}
                currentUsername={user?.username}
              />
            ))}
            {error && <div className="ew-banner ew-banner-error">{error}</div>}
          </>
        )}
      </div>

      <div className="ew-reply-area">
        {composer ? (
          <EmailComposer
            key={`${composer.mode}-${composer.message?.id || 'none'}`}
            mode={composer.mode}
            conversationKey={conversationKey}
            initialSubject={
              composer.mode === 'forward'
                ? forwardSubject(composer.message?.subject)
                : replySubject(composer.message?.subject || view.last_subject)
            }
            replyToMessageId={composer.mode === 'reply' ? composer.message?.id ?? null : null}
            forwardOf={composer.mode === 'forward' ? composer.message : null}
            lastSeenMessageId={composer.mode === 'reply' ? lastSeenMessageId : null}
            templateValues={templateValues}
            signature={signature}
            draftKey={composer.mode === 'reply' ? `reply:${conversationKey}` : null}
            onSent={handleSent}
            onCancel={() => setComposer(null)}
            sendLabel={composer.mode === 'forward' ? 'Forward' : 'Send'}
          />
        ) : (
          <div className="ew-reply-bar">
            <button
              type="button"
              className="ew-reply-bar-input"
              onClick={() => openReply()}
              disabled={loading || messages.length === 0}
            >
              <ReplyIcon width={14} height={14} />
              {email ? `Reply to ${firstName || email}…` : 'No email address for this customer'}
            </button>
            {latest && (
              <button type="button" className="btn btn-secondary btn-small" onClick={() => openForward()}>
                <ForwardIcon width={13} height={13} />
                Forward
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default EmailThread;
